import { useState, useEffect } from "react";
import CardRow from "components/organisms/CardRow/CardRow";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { getAPICall, postAPICall } from "lib/callApi";
import { enqueueSnackbar } from "notistack";
import { FETCH_SUBSCRIPTION_PLANS, CONTACT_SALES_TEAM } from "lib/constants";
import useAuth from "auth/useAuth";
import useWorkspaceSubscription from "hooks/useWorkspaceSubscription";
import MDButton from "components/MDButton";
import { CircularProgress, Stack } from "@mui/material";
import MDBox from "components/MDBox";
import { useNavigate } from "react-router-dom";

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [freeTrialPlan, setFreeTrialPlan] = useState();
  const { isLoading, handleLogout } = useAuth();
  const {
    isLoading: subscriptionLoading,
    subscription,
    createSelfServeSubscription,
    upgradeSubscription,
  } = useWorkspaceSubscription();

  const subscriptionStage = subscription?.subscriptionStage;

  const contactSales = async () => {
    const result = await postAPICall(CONTACT_SALES_TEAM, true);

    if (result?.data?.success) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "success",
          title: "Sit tight!",
          text: "Our enterprise sales team has been contacted and will reach out to you shortly.",
        }),
        {
          variant: "custom",
        }
      );
    } else {
      const responseMessage = result?.data?.message || result?.response?.data?.message;
      enqueueSnackbar(
        JSON.stringify({
          icon: "info",
          title: "Information",
          text: responseMessage,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    const fetchPlans = async () => {
      const result = await getAPICall(FETCH_SUBSCRIPTION_PLANS, true);

      if (result?.data?.success) {
        const formattedPlans = result.data.data;
        let tempFreeTrialPlan;
        const plansWithAttachedActions = formattedPlans
          .map((plan) => {
            if (plan?.callToAction?.action) {
              switch (plan?.callToAction?.action) {
                case "startTrial":
                  plan.callToAction.onClick = () =>
                    subscription?.subscriptionNotActivatedYet
                      ? createSelfServeSubscription(plan)
                      : upgradeSubscription(plan);
                  break;
                case "startTrialContactSales":
                default:
                  plan.callToAction.onClick = contactSales;
                  break;
              }
            }

            if (plan.title === "Team Trial") {
              tempFreeTrialPlan = plan;
              return null;
            }
            return plan;
          })
          .filter((plan) => !!plan);
        setPlans(plansWithAttachedActions);
        setFreeTrialPlan(tempFreeTrialPlan);
      }
    };

    if (!subscriptionLoading && !subscriptionStage) {
      fetchPlans();
    }
  }, [subscriptionLoading, subscriptionStage]);

  if (isLoading || subscriptionLoading) {
    return (
      <Stack alignItems="center">
        <CircularProgress
          sx={{
            position: "fixed",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9,
          }}
          color="info"
          size="10vh"
        />
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        padding: "2rem 4rem",
      }}
    >
      <MDBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ cursor: "pointer" }} onClick={() => navigate("/home")}>
          <img src="/logos/Alysio-Logo-Black.svg" alt="Alysio logo" width={150} />
        </Box>
        <MDButton variant="gradient" color="secondary" size="small" onClick={handleLogout}>
          Sign Out
        </MDButton>
      </MDBox>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingTop: "6rem",
          paddingBottom: "4rem",
        }}
      >
        {subscriptionStage ? (
          <Typography>
            Your order is currently waiting to be counter signed. One of the Alysio team members
            will review and sign shortly.
          </Typography>
        ) : (
          <CardRow cards={plans} freeTrialPlan={freeTrialPlan} />
        )}
      </Box>
    </Box>
  );
};

export default SubscriptionPlans;
